import React, { useState, useEffect } from "react";
import { Box, styled, Modal, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector } from "react-redux";
import { useStepImgDiffQuery } from "../../../useQuery";

const StyledBox = styled(Box)`
  position: absolute;
  padding: 6px 12px;
  border-radius: 25px;
  background: rgba(255, 255, 255, 1);
  backdrop-filter: blur(2px);
  min-width: 150px;
  font-size: 12px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.1);
  color: #444141;
  letter-spacing: 0.9px;
  bottom: 10px;
`;

const ImageContainer = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  position: relative;
  height: 100%;
  background-color: #f5f5f5;
  overflow: hidden;
`;

const StyledImage = styled("img")`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  width: auto;
  height: auto;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: scale(1.02);
  }
`;

const ModalContent = styled(Box)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #000;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 90vw;
  height: 90vh;
  outline: none;
  overflow: auto;
`;

const ModalImage = styled("img")`
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
`;

const CloseButton = styled(IconButton)`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: rgba(255, 255, 255, 0.7);
  z-index: 10;

  &:hover {
    background-color: rgba(255, 255, 255, 0.9);
  }
`;

const Screenshot = ({ fromExcReports, currentEditItem, fromImageDiff }) => {
  const tsrData = useSelector((state) => state.authoringSlice.tsrData);
  const currentEditItemIndex = useSelector((state) => state.authoringSlice.editItemIndex);
  const imgeDiffTcid = useSelector((state) => state.executionSlice.selectedTestCase);

  const [isBeforeImageLoaded, setIsBeforeImageLoaded] = useState(false);
  const [beforeImageSrc, setBeforeImageSrc] = useState(
    tsrData[currentEditItemIndex]?.screenshot1 || "https://via.placeholder.com/900?text=No+Image",
  );

  const [isAfterImageLoaded, setIsAfterImageLoaded] = useState(false);
  const [afterImageSrc, setAfterImageSrc] = useState(
    tsrData[currentEditItemIndex]?.Failure_Screenshot_Location ||
      "https://via.placeholder.com/900?text=No+Image",
  );

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalImage, setModalImage] = useState("");
  const [modalLabel, setModalLabel] = useState("");

  const { refetchWithParams: getImgDiff, data } = useStepImgDiffQuery();

  useEffect(() => {
    if (!fromImageDiff) return;

    getImgDiff({
      tsid: currentEditItem.Test_Step,
      stepNumber: currentEditItem.Step_Number,
      tcid: imgeDiffTcid,
      tcrid: currentEditItem.Test_Case_Results_Id,
    });
  }, [fromImageDiff]);

  const handleBeforeImageLoad = () => setIsBeforeImageLoaded(true);
  const handleBeforeImageError = (e) => {
    e.preventDefault();
    setIsBeforeImageLoaded(false);
    setBeforeImageSrc("https://via.placeholder.com/900?text=No+Image");
  };

  const handleAfterImageLoad = () => setIsAfterImageLoaded(true);
  const handleAfterImageError = (e) => {
    e.preventDefault();
    setIsAfterImageLoaded(false);
    setAfterImageSrc("https://via.placeholder.com/900?text=No+Image");
  };

  useEffect(() => {
    setBeforeImageSrc(
      tsrData[currentEditItemIndex]?.screenshot1 || "https://via.placeholder.com/900?text=No+Image",
    );
    setAfterImageSrc(
      tsrData[currentEditItemIndex]?.Failure_Screenshot_Location ||
        "https://via.placeholder.com/900?text=No+Image",
    );
  }, [tsrData, currentEditItemIndex]);

  // Get the correct image sources based on props
  const getBeforeImageSrc = () => {
    if (fromImageDiff) {
      return data?.image1 || "https://via.placeholder.com/900?text=No+Image";
    } else if (fromExcReports) {
      return currentEditItem.screenshot1 || "https://via.placeholder.com/900?text=No+Image";
    } else {
      return beforeImageSrc;
    }
  };

  const getAfterImageSrc = () => {
    if (fromImageDiff) {
      return data?.image2 || "https://via.placeholder.com/900?text=No+Image";
    } else if (fromExcReports) {
      return (
        currentEditItem.Failure_Screenshot_Location ||
        "https://via.placeholder.com/900?text=No+Image"
      );
    } else {
      return afterImageSrc;
    }
  };

  // Handle image click to open modal
  const handleImageClick = (imageSrc, label) => {
    setModalImage(imageSrc);
    setModalLabel(label);
    setModalOpen(true);
  };

  // Handle modal close
  const handleCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <>
      <Box
        border="2px solid rgba(0, 0, 0, 0.30)"
        borderRadius="12px"
        height={500}
        display="flex"
        overflow="hidden"
      >
        <ImageContainer borderRight="2px solid blue">
          <StyledImage
            src={getBeforeImageSrc()}
            alt="Before"
            onLoad={handleBeforeImageLoad}
            onError={handleBeforeImageError}
            onClick={() =>
              handleImageClick(
                getBeforeImageSrc(),
                fromImageDiff ? "Latest Image" : "Element Selected",
              )
            }
          />
          <StyledBox>{fromImageDiff ? "Latest Image" : "Element Selected"}</StyledBox>
        </ImageContainer>

        <ImageContainer>
          <StyledImage
            src={getAfterImageSrc()}
            alt="After"
            onLoad={handleAfterImageLoad}
            onError={handleAfterImageError}
            onClick={() =>
              handleImageClick(
                getAfterImageSrc(),
                fromImageDiff ? "Previous Image" : "Action Completed",
              )
            }
          />
          <StyledBox>{fromImageDiff ? "Previous Image" : "Action Completed"}</StyledBox>
        </ImageContainer>
      </Box>

      {/* Image Modal */}
      <Modal
        open={modalOpen}
        onClose={handleCloseModal}
        aria-labelledby="image-modal"
        aria-describedby="full-size-image-view"
      >
        <ModalContent>
          <CloseButton onClick={handleCloseModal}>
            <CloseIcon />
          </CloseButton>
          <Box textAlign="center">
            <ModalImage src={modalImage} alt={modalLabel} />
            <StyledBox sx={{ position: "relative", bottom: "auto", marginTop: "10px" }}>
              {modalLabel}
            </StyledBox>
          </Box>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Screenshot;
